"use client";

import { BadgeCheckIcon, Loader2Icon, PhoneIcon, SendIcon } from "lucide-react";
import { useState } from "react";

const WA_HREF_CHAT = "https://wa.me/16812992414";

type SubmitState = "idle" | "loading" | "success" | "error";

export const SupportSection = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setState("loading");
    setErrorMessage(null);
    try {
      const res = await fetch("/api/public/support-contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          whatsapp: whatsapp.trim(),
          message: message.trim(),
        }),
      });
      const data = (await res.json().catch(() => null)) as
        | { ok?: boolean; detail?: string; title?: string }
        | null;
      if (!res.ok) {
        const detail =
          typeof data?.detail === "string"
            ? data.detail
            : res.status === 429
              ? "Demasiados intentos. Esperá un momento e intentá de nuevo."
              : "No se pudo enviar. Probá de nuevo más tarde.";
        setErrorMessage(detail);
        setState("error");
        return;
      }
      setState("success");
      setName("");
      setEmail("");
      setWhatsapp("");
      setMessage("");
    } catch {
      setErrorMessage("Error de red. Revisá tu conexión e intentá de nuevo.");
      setState("error");
    }
  }

  return (
    <section
      id="soporte"
      className="relative mb-8 w-full scroll-mt-28 overflow-hidden rounded-3xl border border-white/10 bg-zinc-950 px-5 py-10 sm:px-8 lg:px-12 lg:py-14"
    >
      <div className="pointer-events-none absolute -left-24 bottom-0 h-56 w-56 rounded-full bg-violet-500/15 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 top-0 h-64 w-64 rounded-full bg-cyan-500/15 blur-3xl" />

      <div className="relative z-10">
        <div className="flex flex-col gap-10 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:items-start lg:gap-12">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold tracking-wide text-cyan-200">
              <BadgeCheckIcon className="h-3.5 w-3.5" />
              Contacto
            </div>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-white sm:text-4xl lg:text-5xl">
              ¿Necesitás ayuda?
              <span className="mt-3 block text-lg font-bold leading-snug tracking-normal text-cyan-300 sm:text-xl">
                Escribinos y te respondemos.
              </span>
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-300 sm:text-base">
              Completá el formulario con tu consulta.
            </p>
{/* 
            <div className="mt-6 rounded-2xl border border-cyan-300/25 bg-linear-to-br from-cyan-400/15 to-violet-500/10 px-4 py-4 text-sm text-cyan-50">
              <p className="font-semibold text-cyan-100">Respuesta rápida</p>
              <p className="mt-1 text-xs leading-relaxed text-cyan-100/85">
                Para temas de compra, créditos o el flujo por WhatsApp, a veces es más directo hablar ahí.
              </p>
              <a
                href={WA_HREF_CHAT}
                className="mt-3 inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2.5 text-xs font-bold text-white transition hover:bg-green-500 sm:text-sm"
              >
                <PhoneIcon className="h-4 w-4 shrink-0" />
                Abrir WhatsApp
              </a>
            </div> */}
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/3 p-5 backdrop-blur-md sm:p-6">
            {state === "success" ? (
              <div className="py-4 text-center text-cyan-50">
                <p className="text-lg font-bold text-white">¡Listo!</p>
                <p className="mt-2 text-sm text-zinc-300">
                  Recibimos tu mensaje. Te vamos a contactar pronto.
                </p>
                <button
                  type="button"
                  onClick={() => setState("idle")}
                  className="mt-6 text-sm font-semibold text-cyan-300 underline-offset-4 hover:underline"
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="flex flex-col gap-4">
                <p className="text-sm font-semibold text-zinc-200">Formulario de contacto</p>
                <div>
                  <label htmlFor="support-name" className="mb-1.5 block text-sm font-medium text-zinc-300">
                    Nombre
                  </label>
                  <input
                    id="support-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    maxLength={120}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-xl border border-white/15 bg-zinc-900/80 px-4 py-3 text-sm text-white outline-none ring-cyan-400/40 placeholder:text-zinc-500 focus:border-cyan-400/50 focus:ring-2"
                    placeholder="Cómo te llamamos"
                  />
                </div>
                <div>
                  <label htmlFor="support-email" className="mb-1.5 block text-sm font-medium text-zinc-300">
                    Email
                  </label>
                  <input
                    id="support-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength={254}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-white/15 bg-zinc-900/80 px-4 py-3 text-sm text-white outline-none ring-cyan-400/40 placeholder:text-zinc-500 focus:border-cyan-400/50 focus:ring-2"
                    placeholder="tu@email.com"
                  />
                </div>
                <div>
                  <label htmlFor="support-wa" className="mb-1.5 block text-sm font-medium text-zinc-300">
                    WhatsApp
                  </label>
                  <input
                    id="support-wa"
                    name="whatsapp"
                    type="tel"
                    autoComplete="tel"
                    required
                    maxLength={40}
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    className="w-full rounded-xl border border-white/15 bg-zinc-900/80 px-4 py-3 text-sm text-white outline-none ring-cyan-400/40 placeholder:text-zinc-500 focus:border-cyan-400/50 focus:ring-2"
                    placeholder="+54 9 11 1234-5678"
                  />
                </div>
                <div>
                  <label htmlFor="support-msg" className="mb-1.5 block text-sm font-medium text-zinc-300">
                    Mensaje
                  </label>
                  <textarea
                    id="support-msg"
                    name="message"
                    rows={4}
                    required
                    maxLength={4000}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full resize-y rounded-xl border border-white/15 bg-zinc-900/80 px-4 py-3 text-sm text-white outline-none ring-cyan-400/40 placeholder:text-zinc-500 focus:border-cyan-400/50 focus:ring-2"
                    placeholder="Contanos en qué podemos ayudarte…"
                  />
                </div>

                {errorMessage && (
                  <p className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                    {errorMessage}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={state === "loading"}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-linear-to-r from-cyan-500 to-violet-600 px-5 py-3.5 text-sm font-bold text-white shadow-lg shadow-cyan-500/15 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {state === "loading" ? (
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <SendIcon className="h-4 w-4" aria-hidden />
                  )}
                  Enviar mensaje
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
